import { defaultDataSource } from './data_source.jsx'
import { JOURNAL_HELPER } from '../utils/journal.jsx' // not working when imported from index in here
import { format } from 'date-fns'
import { DateFormat } from './dev_extreme.jsx'
import { BANK_STATEMENT_HELPER } from '../utils/bank_statement' // not working when imported from index in here
import enumConstants from '../enumConstants.json'

/**
 * @typedef {typeof import('../enumConstants.json').model_types} ModelType
 */

/** @type {ModelType} */
export const MODEL_TYPE = enumConstants.model_types

export const MODEL_TYPE_KEY_BY_VALUE = (value) => {
  return Object.keys(MODEL_TYPE).find(key => MODEL_TYPE[key] === value)
}

const generateDocumentMapper = (type, link) => {
  return {
    [type]: {
      dataSource: (config) => defaultDataSource.document({
        documentTypeBind: {
          link
        },
        ...config
      }),
      displayExpr: (data) => `${data.no} - ${format(new Date(data.date), DateFormat.display.date)} - ${data.total.toFixed(2)}(${data.currency_code})`,
      searchExpr: () => ['no', 'date', 'total', 'currency_code'],
      lineDataSource: (config) => defaultDataSource.document_lines({
        documentTypeBind: {
          link
        },
        documentHeader: {
          id: config.headerId
        },
        ...config
      }),
      lineDisplayExpr: (data) => `${data.line_no} - ${data.description}`,
      lineSearchExpr: () => ['line_no', 'description'],
      ...JOURNAL_HELPER.DOCUMENT,
      ...BANK_STATEMENT_HELPER.DOCUMENT
    }
  }
}

const generateTransferMapper = (type, link) => {
  return {
    [type]: {
      dataSource: (config) => defaultDataSource.transferOrder({
        transferTypeBind: {
          link
        },
        ...config
      }),
      displayExpr: (data) => `${data.no} - ${format(new Date(data.date), DateFormat.display.date)}`,
      searchExpr: () => ['no', 'date'],
      lineDisplayExpr: (data) => `${data.line_no} - ${data.description}`,
      lineSearchExpr: () => ['line_no', 'description']
    }
  }
}

const generateCashierMapper = (type, link) => {
  return {
    [type]: {
      dataSource: (config) => defaultDataSource.cashierHeader({
        cashierTypeBind: {
          link
        },
        ...config
      }),
      displayExpr: (data) => `${data.no} - ${format(new Date(data.date), DateFormat.display.date)} - ${data.total.toFixed(2)}(${data.currency_code})`,
      searchExpr: () => ['no', 'date', 'total', 'currency_code'],
      lineDataSource: (config) => defaultDataSource.cashierLines({
        cashierTypeBind: {
          link
        },
        cashierHeader: {
          id: config.headerId
        },
        ...config
      }),
      lineDisplayExpr: (data) => `${data.line_no} - ${data.description}`,
      lineSearchExpr: () => ['line_no', 'description'],
      ...JOURNAL_HELPER.CASHIER
    }
  }
}

const SALES_MAPPER = {
  ...generateDocumentMapper(MODEL_TYPE.SALES_OPPORTUNITY, 'sales-opportunity'),
  ...generateDocumentMapper(MODEL_TYPE.SALES_QUOTE, 'sales-quote'),
  ...generateDocumentMapper(MODEL_TYPE.SALES_ORDER, 'sales-order'),
  ...generateDocumentMapper(MODEL_TYPE.SALES_SHIPMENT, 'sales-shipment'),
  ...generateDocumentMapper(MODEL_TYPE.SALES_INVOICE, 'sales-invoice'),
  ...generateDocumentMapper(MODEL_TYPE.SALES_RETURN_ORDER, 'sales-return-order'),
  ...generateDocumentMapper(MODEL_TYPE.SALES_RETURN_SHIPMENT, 'sales-return-shipment'),
  ...generateDocumentMapper(MODEL_TYPE.SALES_CREDIT_MEMO, 'sales-credit-memo'),
  ...generateDocumentMapper(MODEL_TYPE.SALES_FINANCE_CHARGE, 'sales-finance-charge'),
  ...generateDocumentMapper(MODEL_TYPE.SALES_CONTRACT, 'sales-contract'),
  ...generateDocumentMapper(MODEL_TYPE.SALES_ADVANCE_INVOICE, 'sales-advance-invoice')
}

const PURCHASE_MAPPER = {
  ...generateDocumentMapper(MODEL_TYPE.PURCHASE_QUOTE, 'purchase-quote'),
  ...generateDocumentMapper(MODEL_TYPE.PURCHASE_ORDER, 'purchase-order'),
  ...generateDocumentMapper(MODEL_TYPE.PURCHASE_RECEIPT, 'purchase-receipt'),
  ...generateDocumentMapper(MODEL_TYPE.PURCHASE_INVOICE, 'purchase-invoice'),
  ...generateDocumentMapper(MODEL_TYPE.PURCHASE_RETURN_ORDER, 'purchase-return-order'),
  ...generateDocumentMapper(MODEL_TYPE.PURCHASE_RETURN_RECEIPT, 'purchase-return-receipt'),
  ...generateDocumentMapper(MODEL_TYPE.PURCHASE_CREDIT_MEMO, 'purchase-credit-memo'),
  ...generateDocumentMapper(MODEL_TYPE.PURCHASE_FINANCE_CHARGE, 'purchase-finance-charge'),
  ...generateDocumentMapper(MODEL_TYPE.PURCHASE_CONTRACT, 'purchase-contract'),
  ...generateDocumentMapper(MODEL_TYPE.PURCHASE_RETAIL_TRANSACTION, 'purchase-retail-transaction'),
  ...generateDocumentMapper(MODEL_TYPE.PURCHASE_RETAIL_REFUND_TRANSACTION, 'purchase-retail-refund-transaction'),
  ...generateDocumentMapper(MODEL_TYPE.PURCHASE_ADVANCE_INVOICE, 'purchase-advance-invoice')
}

const SALES_RETAIL_MAPPER = {
  ...generateDocumentMapper(MODEL_TYPE.SALES_RETAIL_TRANSACTION, 'sales-retail-transaction'),
  ...generateDocumentMapper(MODEL_TYPE.SALES_RETAIL_REFUND_TRANSACTION, 'sales-retail-refund-transaction'),
  ...generateDocumentMapper(MODEL_TYPE.SALES_RETAIL_ON_HOLD_TRANSACTION, 'sales-retail-on-hold-transaction')
}

const SERVICE_MAPPER = {
  ...generateDocumentMapper(MODEL_TYPE.SERVICE_QUOTE, 'service-quote'),
  ...generateDocumentMapper(MODEL_TYPE.SERVICE_CONTRACT, 'service-contract'),
  ...generateDocumentMapper(MODEL_TYPE.SERVICE_ORDER, 'service-order'),
  ...generateDocumentMapper(MODEL_TYPE.SERVICE_INVOICE, 'service-invoice'),
  ...generateDocumentMapper(MODEL_TYPE.SERVICE_SHIPMENT, 'service-shipment')
}

const TRANSFER_MAPPER = {
  ...generateTransferMapper(MODEL_TYPE.TRANSFER_ORDER, 'transfer-order'),
  ...generateTransferMapper(MODEL_TYPE.TRANSFER_ORDER_SHIPMENT, 'transfer-order-shipment'),
  ...generateTransferMapper(MODEL_TYPE.TRANSFER_ORDER_RECEIPT, 'transfer-order-receipt')
}

const CASHIER_MAPPER = {
  ...generateCashierMapper(MODEL_TYPE.CASHIER_RECEIVES, 'cashier-receives'),
  ...generateCashierMapper(MODEL_TYPE.CASHIER_PAYS, 'cashier-pays')
}

export const MODEL_TYPE_MAPPER = {
  ...SALES_MAPPER,
  ...PURCHASE_MAPPER,
  ...SALES_RETAIL_MAPPER,
  ...SERVICE_MAPPER,
  ...TRANSFER_MAPPER,
  ...CASHIER_MAPPER,
  [MODEL_TYPE.PHYSICAL_INVENTORY]: {
    dataSource: (config) => defaultDataSource.physicalInventory(config),
    displayExpr: (data) => `${data.no} - ${format(new Date(data.date), DateFormat.display.date)}`,
    searchExpr: () => ['no', 'date'],
    lineDataSource: (config) => defaultDataSource.physicalInventoryLines({
      physicalInventoryHeader: {
        id: config.headerId
      },
      ...config
    }),
    lineDisplayExpr: (data) => `${data.line_no} - ${data.description}`,
    lineSearchExpr: () => ['line_no', 'description'],
    ...JOURNAL_HELPER.PHYSICAL_INVENTORY
  },
  [MODEL_TYPE.PRODUCT]: {
    dataSource: (config) => defaultDataSource.product(config),
    displayExpr: (data, languageCode) => data.name[languageCode],
    searchExpr: (languageCode) => `name->>"$.${languageCode}"`
  },
  [MODEL_TYPE.FIXED_ASSET]: {
    dataSource: (config) => defaultDataSource.fixedAsset(config),
    displayExpr: (data) => `${data.no} - ${data.name}`,
    searchExpr: () => ['no', 'name']
  },
  [MODEL_TYPE.RESOURCE]: {
    dataSource: (config) => defaultDataSource.resource(config),
    displayExpr: (data) => data.name,
    searchExpr: () => 'name'
  },
  [MODEL_TYPE.SMALL_INVENTORY]: {
    dataSource: (config) => defaultDataSource.smallInventory(config),
    displayExpr: (data) => data.name,
    searchExpr: () => 'name'
  },
  [MODEL_TYPE.ACCOUNT]: {
    dataSource: (config) => defaultDataSource.account(config),
    displayExpr: (data) => data.name,
    searchExpr: () => 'name'
  },
  [MODEL_TYPE.BANK_ACCOUNT]: {
    dataSource: (config) => defaultDataSource.bank_account(config),
    displayExpr: (data) => data.bank_account_number + ' - ' + data.bank?.name,
    searchExpr: () => 'bank_account_number'
  },
  [MODEL_TYPE.EMPLOYEE]: {
    dataSource: (config) => defaultDataSource.employee(config),
    displayExpr: (data) => data.display_name,
    searchExpr: () => 'display_name'
  },
  [MODEL_TYPE.CASH_ACCOUNT]: {
    dataSource: (config) => defaultDataSource.cashAccount(config),
    displayExpr: (data) => data.name,
    searchExpr: () => 'name'
  },
  [MODEL_TYPE.COMPANY]: {
    dataSource: (config) => defaultDataSource.company(config),
    displayExpr: (data) => data.name,
    searchExpr: () => 'name'
  },
  [MODEL_TYPE.CHART_OF_ACCOUNT]: {
    dataSource: (config) => defaultDataSource.chartOfAccount(config),
    displayExpr: (data) => `${data.no} - ${data.name}`,
    searchExpr: () => ['no', 'name']
  },
  [MODEL_TYPE.BANK_STATEMENT]: {
    dataSource: (config) => defaultDataSource.bank_statement(config),
    displayExpr: (data) => `${data.no} - ${format(new Date(data.date), DateFormat.display.date)} - ${data.bank_account_display}`,
    searchExpr: () => ['no', 'date', 'bank_account_display'],
    lineDataSource: (config) => defaultDataSource.bank_statement_lines({
      bankStatement: {
        id: config.headerId
      },
      ...config
    }),
    lineDisplayExpr: (data) => `${data.header_no} - ${format(new Date(data.header_date), DateFormat.display.date)} - ${data.header_bank_account_display} - ${data.line_no}`,
    lineSearchExpr: () => ['header_no', 'header_date', 'header_bank_account_display', 'line_no'],
    ...JOURNAL_HELPER.BANK_STATEMENT
  },
  [MODEL_TYPE.RESOURCE_GROUP]: {
    dataSource: (config) => defaultDataSource.resourceGroup(config),
    displayExpr: (data) => data.name,
    searchExpr: () => 'name'
  },
  [MODEL_TYPE.RECLASSIFICATION_ORDER]: {
    displayExpr: (data) => `${data.no} - ${format(new Date(data.date), DateFormat.display.date)} - ${data.total.toFixed(2)}(${data.currency_code})`,
    searchExpr: () => ['no', 'date', 'total', 'currency_code'],
    lineDisplayExpr: (data) => `${data.line_no} - ${data.description}`,
    lineSearchExpr: () => ['line_no', 'description']
  },
  [MODEL_TYPE.SALARY_CALCULATION]: {
    dataSource: (config) => defaultDataSource.salaryCalculationHeader(config),
    displayExpr: (data) => `${data.no} - ${format(new Date(data.date), DateFormat.display.date)}`,
    searchExpr: () => ['no', 'date']
  },
  [MODEL_TYPE.PERSON_PAYMENT]: {
    dataSource: (config) => defaultDataSource.personPaymentHeader(config),
    displayExpr: (data) => `${data.no} - ${format(new Date(data.date), DateFormat.display.date)}`,
    searchExpr: () => ['no', 'date']
  }
}

export const modelDataSource = (company, model, type) => {
  let helper = null

  if (type) {
    helper = MODEL_TYPE_MAPPER[type]
  }

  let store = null

  if (helper) {
    const params = {
      searchExpr: helper.searchExpr(company.language_code),
      filter: []
    }

    store = helper.dataSource({ model, params })
  }

  return {
    store: store || [],
    pageSize: 20,
    paginate: true
  }
}
